import type { FC } from 'react'
import React, { memo } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import {
  formatStatisticsScore,
  getChartTooltipDate,
  isEqual,
} from '@homeviews/utils'
import BenchmarkHorizontalLayer from '../BenchmarkHorizontalLayer'

import {
  isEngageUser,
  useGetAdditionalQuestionsBenchmarkQuery,
  useGetReviewsStatsQuery,
} from '@homeviews/store'

import { transformAdditionalQuestionsChartData } from '../../helpers/transform'
import { CHART_BRAND_COLORS } from '../../helpers/settings'
import ChartLegend from '../ChartLegend'
import { getPreviousPeriod, swapObjectKeysValues } from '@homeviews/utils'
import { Spinner } from '@homeviews/shared-ui'
import NoData from '../NoData'
import { chartTheme } from '../../helpers/layout'
import ChartTooltip from '../ChartTooltip'
import { useSelector } from 'react-redux'

interface IProps {
  activeFilters: any
  graphDisabled?: boolean
  gridRef?: HTMLElement
  isBtr?: boolean
}

const BTR_QUESTIONS_MAP = {
  tenant_recommends: 'Recommend Landlord',
}

const BTS_QUESTIONS_MAP = {
  owner_recommends: 'Recommend Developer',
}

const format = (v) => `${v}%`

const AdditionalQuestionsChart: FC<IProps> = memo(
  ({ activeFilters, graphDisabled, gridRef, isBtr }) => {
    const { dateFrom: prevDateFrom, dateTo: prevDateTo } = getPreviousPeriod(
      activeFilters?.date_from,
      activeFilters?.date_to,
    )

    const isEngagePackage = Boolean(useSelector(isEngageUser))

    const {
      data: tenantAdditionalQuestions,
      isFetching: isFetchingTenantAdditionalQuestions,
    } = useGetReviewsStatsQuery(
      {
        'reviewer_type[]': isBtr ? 'tenant' : ['owner', 'shared_owner'],
        'development_type[]': isBtr ? 'btr' : ['bts', 'hd', 'll', 'ha'],
        aggregate: 'avg',
        aggregate_fieldset: isBtr ? 'tenant' : 'owner',
        ...activeFilters,
        engage: isEngagePackage,
      },
      {
        skip: graphDisabled || isBtr === undefined,
      },
    )

    const {
      data: prevTenantAdditionalQuestions,
      isFetching: isFetchingPrevTenantAdditionalQuestions,
    } = useGetReviewsStatsQuery(
      {
        'reviewer_type[]': isBtr ? 'tenant' : ['owner', 'shared_owner'],
        'development_type[]': isBtr ? 'btr' : ['bts', 'hd', 'll', 'ha'],
        aggregate: 'avg',
        aggregate_fieldset: isBtr ? 'tenant' : 'owner',
        ...activeFilters,
        date_from: prevDateFrom,
        date_to: prevDateTo,
        engage: isEngagePackage,
      },
      {
        skip: graphDisabled || isBtr === undefined,
      },
    )

    const { data: benchmarkData } = useGetAdditionalQuestionsBenchmarkQuery(
      {
        'reviewer_type[]': isBtr ? 'tenant' : ['owner', 'shared_owner'],
        'development_type[]': isBtr ? 'btr' : ['bts', 'hd', 'll', 'ha'],
        aggregate: 'avg',
        aggregate_fieldset: isBtr ? 'tenant' : 'owner',
        ...activeFilters,
        engage: isEngagePackage,
      },
      {
        skip: graphDisabled || isBtr === undefined,
      },
    )

    const chartData = transformAdditionalQuestionsChartData(
      tenantAdditionalQuestions?.data,
      prevTenantAdditionalQuestions?.data,
      benchmarkData?.data,
      isBtr ? BTR_QUESTIONS_MAP : BTS_QUESTIONS_MAP,
      isBtr,
    )

    if (!chartData || !chartData?.length) {
      if (gridRef) {
        gridRef.style.display = 'none'
      }
      return <NoData />
    } else {
      if (gridRef) {
        gridRef.style.display = 'block'
      }
    }

    if (
      isFetchingTenantAdditionalQuestions ||
      isFetchingPrevTenantAdditionalQuestions
    ) {
      return <Spinner centered />
    }

    const periodFilter = true

    let formattedDateRange =
      !prevDateFrom || !prevDateTo
        ? ''
        : `${getChartTooltipDate(prevDateFrom)} - ${getChartTooltipDate(
            prevDateTo,
          )}`

    if (formattedDateRange) {
      formattedDateRange = `Previous Period (${formattedDateRange})`
    }

    let hasPrevData = false
    let chartKeys = ['Current Rating']
    Object.keys(isBtr ? BTR_QUESTIONS_MAP : BTS_QUESTIONS_MAP).map(
      (itemKey: any) => {
        if (
          prevTenantAdditionalQuestions?.data?.[itemKey] &&
          prevTenantAdditionalQuestions?.data?.[itemKey] !== '0.00'
        ) {
          hasPrevData = true
        }
      },
    )

    if (hasPrevData) {
      chartKeys = ['Current Rating', 'Previous Year Average']
    }

    return (
      <div className="l-chartContainer">
        <div className="l-chartWrapper">
          <ResponsiveBar
            data={chartData}
            keys={chartKeys}
            colors={[
              CHART_BRAND_COLORS.green1,
              CHART_BRAND_COLORS.darkBlue3,
              CHART_BRAND_COLORS.gold2,
            ]}
            isInteractive={true}
            enableGridX={false}
            enableLabel={true}
            groupMode="grouped"
            padding={0.65}
            innerPadding={10}
            maxValue={100}
            theme={chartTheme}
            axisBottom={{
              tickSize: 0,
              tickPadding: 10,
              tickRotation: 0,
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 10,
              tickRotation: 0,
              tickValues: [0, 20, 40, 60, 80, 100],
              format,
            }}
            margin={{ top: 50, right: 20, bottom: 30, left: 50 }}
            label={(labelValue) => {
              return (
                <tspan
                  y={-12}
                  style={{
                    color: '#4E516B',
                    fontSize: '14px',
                    fontWeight: 500,
                    letterSpacing: 0,
                    lineHeight: '16px',
                  }}
                >
                  {formatStatisticsScore(labelValue.formattedValue) + '%'}
                </tspan>
              ) as unknown as string
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor="#4E516B"
            animate={false}
            borderRadius={5}
            layers={[
              'grid',
              'axes',
              (layerProps) => (
                <BenchmarkHorizontalLayer
                  dataProps={{
                    data: benchmarkData?.data?.[isBtr ? 'tenant' : 'owner'],
                    labels: swapObjectKeysValues(
                      isBtr ? BTR_QUESTIONS_MAP : BTS_QUESTIONS_MAP,
                    ),
                    valueFormatter: (value) => `${value * 100}`,
                  }}
                  layerProps={layerProps}
                />
              ),
              'bars',
              'markers',
              'legends',
              'annotations',
            ]}
            tooltip={(tooltip: any) => {
              let tooltipText = ''
              if (tooltip?.id === 'Previous Year Average') {
                tooltipText = periodFilter
                  ? formattedDateRange
                  : `Previous Year Rating`
              } else {
                tooltipText = tooltip.id
              }
              return (
                <ChartTooltip>
                  <ChartLegend
                    direction="vertical"
                    data={[
                      {
                        text: `${tooltipText}: ${
                          tooltip?.formattedValue
                            ? `${formatStatisticsScore(
                                tooltip.formattedValue,
                              )}%`
                            : ''
                        }`,
                        shapeColor: tooltip?.color,
                      },
                    ]}
                  />
                </ChartTooltip>
              )
            }}
          />
        </div>
        <ChartLegend
          data={[
            {
              text: 'Current Rating',
              shapeColor: CHART_BRAND_COLORS.green1,
            },
            hasPrevData && {
              text: periodFilter ? formattedDateRange : `Previous Year Rating`,
              shapeColor: CHART_BRAND_COLORS.darkBlue3,
            },
            {
              text: `Benchmark (${isBtr ? 'Build to Rent' : 'Build to Sell'})`,
              shapeColor: CHART_BRAND_COLORS.gold2,
              tooltipText: isBtr
                ? 'Discover the HomeViews UK benchmark specific to Build to Rent. This is based on all time data up to the last full half year period.'
                : 'Discover the HomeViews UK benchmark specific, based upon reviews in Build to Sell properties. This is based on all time data up to the last full half year period.',
            },
          ]}
        />
      </div>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default AdditionalQuestionsChart
